import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { Alert, CircularProgress } from "@mui/material";
import { format } from "date-fns";

import subscriptionService from "../../core/services/subscriptionService";
import { COMPONENT_WISE_AG_GRID_CONTAINER_HEIGHT } from "../../core/constants/erpConstants";

function formatConsumedAt(consumedAt) {
  if (!consumedAt) return "Date not recorded";
  const consumedDate = new Date(consumedAt);
  if (Number.isNaN(consumedDate.getTime())) return "Date not recorded";
  return format(consumedDate, "dd MMM yyyy, h:mm a");
}

export function QuantityRenderer(params) {
  const balance = params.data;
  const quantityEventName = `membership-quantity-${balance.serviceId}`;
  return (
    <div
      className="d-flex align-items-center justify-content-center"
      style={{ width: "100%", height: "100%" }}
    >
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        className="form-control membership-quantity-input"
        defaultValue={balance.useQuantity || ""}
        aria-label={`Use quantity for ${balance.serviceName}`}
        onInput={(event) => {
          const quantity = event.currentTarget.value.replace(/\D/g, "");
          event.currentTarget.value = quantity;
          balance.useQuantity = quantity;
          window.dispatchEvent(
            new CustomEvent(quantityEventName, {
              detail: quantity,
            }),
          );
        }}
        onMouseDown={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
        onKeyUp={(event) => event.stopPropagation()}
        style={{
          width: 105,
          height: 32,
          minHeight: 32,
          margin: 0,
          padding: "4px 10px",
          lineHeight: "20px",
          boxSizing: "border-box",
        }}
      />
    </div>
  );
}

export function ActionRenderer(params) {
  const balance = params.data;
  const [consuming, setConsuming] = useState(false);
  const [quantity, setQuantity] = useState(balance.useQuantity || "");
  const numericQuantity = Number(quantity);
  const hasValidQuantity =
    Number.isInteger(numericQuantity) &&
    numericQuantity > 0 &&
    numericQuantity <= Number(balance.availableQuantity);

  useEffect(() => {
    const quantityEventName = `membership-quantity-${balance.serviceId}`;
    const handleQuantityChange = (event) => setQuantity(event.detail);
    window.addEventListener(quantityEventName, handleQuantityChange);
    return () =>
      window.removeEventListener(quantityEventName, handleQuantityChange);
  }, [balance.serviceId]);

  const handleConsume = async (event) => {
    event.stopPropagation();
    if (consuming) return;
    setConsuming(true);
    try {
      await params.context.consume(balance);
    } finally {
      setConsuming(false);
    }
  };

  return (
    <div
      className="d-flex align-items-center justify-content-center"
      style={{ width: "100%", height: "100%" }}
    >
      <button
        type="button"
        className="btn btn-primary btn-sm"
        onMouseDown={(event) => event.stopPropagation()}
        onClick={handleConsume}
        disabled={consuming || !hasValidQuantity}
        style={{
          minWidth: 105,
          height: 32,
          margin: 0,
          padding: "4px 12px",
          lineHeight: "20px",
          boxSizing: "border-box",
          textTransform: "uppercase",
        }}
      >
        {consuming ? "Consuming..." : "Consume"}
      </button>
    </div>
  );
}

export default function MembershipServices({
  patientId,
  visitId,
  readOnly = false,
  serverResposnes,
}) {
  const [balances, setBalances] = useState([]);
  const [usageHistory, setUsageHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadBalances = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    setError("");
    try {
      const response = await subscriptionService.getSubscriptionBalances(
        patientId,
      );
      setBalances(Array.isArray(response) ? response : []);
    } catch (apiError) {
      setBalances([]);
      setError(apiError || "Unable to load membership balances");
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  const loadUsageHistory = useCallback(async () => {
    if (!patientId || !visitId) return;
    setLoading(true);
    setError("");
    try {
      const firstPage = await subscriptionService.getSubscriptionUsageHistory(
        patientId,
        visitId,
        0,
      );
      let visitUsage = firstPage?.content || [];
      const totalPages = Number(firstPage?.totalPages || 0);

      // The API is server-paginated. Load the remaining rows so AG Grid can
      // retain the same filtering and pagination used by the active view.
      for (let page = 1; page < totalPages; page += 1) {
        const response =
          await subscriptionService.getSubscriptionUsageHistory(
            patientId,
            visitId,
            page,
          );
        visitUsage = visitUsage.concat(response?.content || []);
      }

      setUsageHistory(visitUsage);
    } catch (apiError) {
      setUsageHistory([]);
      setError(apiError || "Unable to load membership usage for this visit");
    } finally {
      setLoading(false);
    }
  }, [patientId, visitId]);

  useEffect(() => {
    if (readOnly) {
      loadUsageHistory();
    } else {
      loadBalances();
    }
  }, [loadBalances, loadUsageHistory, readOnly]);

  const showMessage = (message, type) => {
    if (typeof serverResposnes === "function") {
      serverResposnes(message, type);
    }
  };

  const consume = async (balance) => {
    const quantity = Number(balance.useQuantity);
    if (!Number.isInteger(quantity) || quantity <= 0) {
      showMessage("Enter a valid quantity to consume", "error");
      return;
    }
    if (quantity > Number(balance.availableQuantity)) {
      showMessage(
        "Quantity cannot exceed the available membership balance",
        "error",
      );
      return;
    }

    try {
      const response = await subscriptionService.consumeSubscription({
        patientId: Number(patientId),
        visitId: Number(visitId),
        serviceId: Number(balance.serviceId),
        quantity,
      });
      const completedMembershipCount = Number(
        response.completedMembershipCount || 0,
      );
      const completionMessage =
        completedMembershipCount === 1
          ? " Membership completed: all included services have been used."
          : completedMembershipCount > 1
            ? ` ${completedMembershipCount} memberships completed: all included services have been used.`
            : "";
      showMessage(
        `${balance.serviceName}: quantity ${quantity} consumed successfully. Remaining quantity: ${response.remainingQuantity}.${completionMessage}`,
        "success",
      );
      await loadBalances();
    } catch (apiError) {
      showMessage(
        apiError || "Unable to consume membership service",
        "error",
      );
    }
  };

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Service",
        field: "serviceName",
        minWidth: 220,
        headerClass: "membership-grid-header-left",
        cellStyle: {
          display: "flex",
          alignItems: "center",
          textAlign: "left",
        },
      },
      {
        headerName: "Entitled",
        field: "entitledQuantity",
        headerClass: "membership-grid-header-center",
        cellStyle: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
        },
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Consumed",
        field: "consumedQuantity",
        headerClass: "membership-grid-header-center",
        cellStyle: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
        },
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Available",
        field: "availableQuantity",
        headerClass: "membership-grid-header-center",
        cellStyle: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          fontWeight: 600,
        },
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Use Quantity",
        field: "useQuantity",
        headerClass: "membership-grid-header-center",
        cellStyle: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          paddingTop: 0,
          paddingBottom: 0,
        },
        cellRenderer: "QuantityRenderer",
        filter: "agNumberColumnFilter",
        sortable: false,
        suppressKeyboardEvent: () => true,
      },
      {
        headerName: "Action",
        field: "action",
        headerClass: "membership-grid-header-center",
        cellStyle: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          paddingTop: 0,
          paddingBottom: 0,
        },
        cellRenderer: "ActionRenderer",
        valueGetter: () => "Consume",
        sortable: false,
      },
    ],
    [],
  );

  const usageColumnDefs = useMemo(
    () => [
      {
        headerName: "Service",
        field: "serviceName",
        minWidth: 220,
        valueGetter: (params) =>
          params.data.serviceName || `Service ${params.data.serviceId}`,
      },
      {
        headerName: "Quantity",
        field: "quantity",
        headerClass: "membership-grid-header-center",
        cellStyle: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
        },
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Consumed Date & Time",
        field: "consumedAt",
        minWidth: 240,
        valueFormatter: (params) => formatConsumedAt(params.value),
      },
    ],
    [],
  );

  const gridContext = useMemo(() => ({}), []);
  gridContext.consume = consume;

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      flex: 1,
      filter: true,
      resizable: true,
      floatingFilter: true,
    }),
    [],
  );

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center p-5">
        <CircularProgress size={32} />
      </div>
    );
  }

  if (readOnly) {
    return (
      <div
        className="ms-1"
        style={{
          width: "100%",
          height: COMPONENT_WISE_AG_GRID_CONTAINER_HEIGHT.MembershipServices,
        }}
      >
        {error && <Alert severity="error">{error}</Alert>}
        {!error && (
          <div className="ag-theme-balham ag-style ag-header-left membership-services-grid px-1 py-2 w-100 h-100">
            <style>{`
              .membership-services-grid .membership-grid-header-center .ag-header-cell-label {
                justify-content: center !important;
                text-align: center !important;
              }

              .membership-services-grid .ag-cell-wrapper,
              .membership-services-grid .ag-cell-value {
                height: 100%;
                width: 100%;
              }
            `}</style>
            <AgGridReact
              rowData={usageHistory}
              columnDefs={usageColumnDefs}
              defaultColDef={defaultColDef}
              pagination
              paginationPageSize={10}
              rowHeight={52}
              headerHeight={34}
              floatingFiltersHeight={34}
              suppressRowClickSelection
              overlayNoRowsTemplate="No membership services were consumed during this visit"
              getRowNodeId={(data) => String(data.id)}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className="ms-1"
      style={{
        width: "100%",
        height: COMPONENT_WISE_AG_GRID_CONTAINER_HEIGHT.MembershipServices,
      }}
    >
      {error && <Alert severity="error">{error}</Alert>}
      {!error && (
        <div className="ag-theme-balham ag-style ag-header-left membership-services-grid px-1 py-2 w-100 h-100">
          <style>{`
            .membership-services-grid .membership-grid-header-center .ag-header-cell-label {
              justify-content: center !important;
              text-align: center !important;
            }

            .membership-services-grid .membership-grid-header-left .ag-header-cell-label {
              justify-content: flex-start !important;
              text-align: left !important;
            }

            .membership-services-grid .ag-cell-wrapper,
            .membership-services-grid .ag-cell-value {
              height: 100%;
              width: 100%;
            }
          `}</style>
          <AgGridReact
            rowData={balances}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            frameworkComponents={{ QuantityRenderer, ActionRenderer }}
            context={gridContext}
            pagination
            paginationPageSize={10}
            rowHeight={52}
            headerHeight={34}
            floatingFiltersHeight={34}
            suppressRowClickSelection
            overlayNoRowsTemplate="This patient has no available membership services"
            getRowNodeId={(data) => String(data.serviceId)}
          />
        </div>
      )}
    </div>
  );
}
